import { FC, useState } from "react";
import toast from "react-hot-toast";
import { setDisputeOperatorPayment } from "../../services/Reservations.service";
import { resolveError } from "../../utils/apiErrorMessage";
import { errorCode } from "../../utils/reservations/cancellation";
import { operatorPaymentRefusalSentence } from "../../utils/reservations/cancellationText";
import Button from "../ui/Button";

const WITHHOLD_WARNING =
  "Pjesa e operatorit për këtë rezervim zbritet nga fatura e tij e ardhshme. Mund ta ktheni këtë " +
  "vendim vetëm derisa kjo zbritje të shfaqet në një faturë të operatorit. Vazhdo?";
const PAY_WARNING =
  "Zbritja hiqet dhe operatori paguhet për këtë rezervim. Kjo bëhet vetëm nëse zbritja nuk është " +
  "shfaqur ende në një faturë të operatorit. Vazhdo?";

// After a lost dispute the operator is paid by default; a super admin may withhold its share
// (deducted on its next invoice) and pay it again until that deduction is invoiced.
const DisputeOperatorPayment: FC<{ uniqueJourneyId: string; withheld: boolean; onDone: () => void }> = ({
  uniqueJourneyId,
  withheld,
  onDone
}) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const decide = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      await setDisputeOperatorPayment(uniqueJourneyId, withheld);
      toast.success(
        withheld ? "Ndalimi u hoq; operatori paguhet për këtë rezervim." : "Pagesa e operatorit u ndal."
      );
      onDone();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const code = errorCode(err);
      setRefusal(
        status === 409
          ? operatorPaymentRefusalSentence(code)
          : resolveError(err, "Pagesa e operatorit nuk u ndryshua. Provoni përsëri.")
      );
      // The dispute is no longer lost, so the page shows a choice that no longer applies.
      if (status === 409 && code === "dispute.not_lost") {
        toast.error(operatorPaymentRefusalSentence(code));
        onDone();
      }
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <strong className="text-black">
          {withheld ? "Pagesa e operatorit është ndalur" : "Operatori paguhet"}
        </strong>
        {!confirming && (
          <Button btnType="custom" padding="px-2 py-1" className="border border-gray-400 bg-white text-xs"
            onClick={() => setConfirming(true)}>
            {withheld ? "Hiq ndalimin e pagesës" : "Ndalo pagesën e operatorit"}
          </Button>
        )}
      </div>
      {confirming && (
        <div className="flex flex-wrap items-center justify-end gap-2 text-xs text-gray-700">
          <span>{withheld ? PAY_WARNING : WITHHOLD_WARNING}</span>
          <Button padding="px-2 py-1" className="text-xs" disabled={busy} onClick={decide}>
            {busy ? "Duke kryer…" : withheld ? "Po, hiqe ndalimin" : "Po, ndalo"}
          </Button>
          <Button btnType="custom" padding="px-2 py-1" className="border border-gray-300 bg-white text-xs"
            disabled={busy} onClick={() => setConfirming(false)}>
            Jo
          </Button>
        </div>
      )}
      {refusal && <p role="alert" className="text-xs text-red-700">{refusal}</p>}
    </div>
  );
};

export default DisputeOperatorPayment;
